import { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper, SortingState, Table } from '@tanstack/react-table';

import Checkbox from 'components/shared/form/Checkbox';
import Menu from 'components/shared/menu';
import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import usePagination from 'hooks/usePagination';
import { GroupPayerNetworkStatus, NetworkStatus, PAYER_TYPES, StatusField } from 'models/GroupPayerNetworkStatus';
import { PERMISSIONS } from 'models/Profile';
import {
  groupPayerNetworkStatusQueryKeys,
  updateGroupPayerNetworkStatus,
} from 'services/api/groupPayerNetworkStatuses';
import { validationErrorMessage } from 'services/api/validationErrors';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

import { KebabButton, RemoveItem } from './facilityNetworkStatusColumns';

// The page's subject. A payer page lists facilities; a facility page lists payers.
export type NetworkStatusKind = 'payer' | 'facility';

export const rowName = (kind: NetworkStatusKind, row: GroupPayerNetworkStatus) =>
  kind === 'payer' ? row.groupName : row.stediPayerName;

// The API param that searches and sorts the name column.
export const nameParam = (kind: NetworkStatusKind) => (kind === 'payer' ? 'groupName' : 'stediPayerName');

// A 404 on a write means the row was removed by someone else.
export const removedElsewhere = (error: unknown) => isAxiosError(error) && error.response?.status === 404;

const STATUS_OPTIONS: { value: NetworkStatus; label: string }[] = [
  { value: 'in', label: 'In' },
  { value: 'out', label: 'Out' },
  { value: null, label: 'Not Set' },
];

const column = createColumnHelper<GroupPayerNetworkStatus>();

// Figma's rows are 54px: the shared 15px cell padding around a 32px control would make them 65px.
const ROW_PADDING = { paddingTop: 11, paddingBottom: 10 };
const cellMeta = { style: ROW_PADDING };

type NetworkStatusTableProps = {
  kind: NetworkStatusKind;
  rows: GroupPayerNetworkStatus[];
  loading: boolean;
  pageCount: number;
  pagination: ReturnType<typeof usePagination>;
  sorting: { sortBy: SortingState; setSortBy: (sort: SortingState) => void };
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  onRemove: (row: GroupPayerNetworkStatus) => void;
};

// What the cells read on each render. Through the table's meta, not the column defs: flexRender
// mounts each cell function as a component, so rebuilding the defs remounts every cell and drops focus.
type CellState = {
  selected: Set<string>;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  pending: Record<string, Partial<GroupPayerNetworkStatus>>;
  edit: (id: string, field: StatusField, value: NetworkStatus) => void;
  toggle: (id: string) => void;
  toggleAll: () => void;
  onRemove: (row: GroupPayerNetworkStatus) => void;
};

const cellState = (table: Table<GroupPayerNetworkStatus>) => table.options.meta as CellState;

function columnsFor(kind: NetworkStatusKind, canEdit: boolean) {
  const name = column.accessor((row) => rowName(kind, row), {
    id: nameParam(kind),
    header: kind === 'payer' ? 'Facility' : 'Payer',
    cell: (info) => <Name>{info.getValue()}</Name>,
    size: 240,
    meta: cellMeta,
  });

  const statuses = PAYER_TYPES.map(({ field, label }) =>
    column.accessor(field, {
      id: field,
      header: label,
      enableSorting: false,
      cell: ({ row: { original }, table }) => {
        const { pending, edit } = cellState(table);
        const sent = pending[original.id]?.[field];
        return (
          <StatusToggle
            label={`${label} for ${rowName(kind, original)}`}
            value={sent === undefined ? original[field] : sent}
            disabled={!canEdit}
            onChange={(value) => edit(original.id, field, value)}
          />
        );
      },
      size: 180,
      meta: cellMeta,
    })
  );

  if (!canEdit) return [name, ...statuses];

  return [
    column.display({
      id: 'select',
      enableSorting: false,
      header: ({ table }) => {
        const { allVisibleSelected, someVisibleSelected, toggleAll } = cellState(table);
        return (
          <SelectCheckbox
            checked={allVisibleSelected}
            checkedColor={colors.blue100}
            indeterminate={someVisibleSelected && !allVisibleSelected}
            size={14}
            overrideSetValue
            onChange={toggleAll}
            dataTestId='network-status-select-all'
          />
        );
      },
      cell: ({ row: { original }, table }) => {
        const { selected, toggle } = cellState(table);
        return (
          <SelectCheckbox
            checked={selected.has(original.id)}
            checkedColor={colors.blue100}
            size={14}
            overrideSetValue
            onChange={() => toggle(original.id)}
            dataTestId={`network-status-select-${original.id}`}
          />
        );
      },
      size: 48,
      meta: cellMeta,
    }),
    name,
    ...statuses,
    column.display({
      id: 'menu',
      enableSorting: false,
      cell: ({ row: { original }, table }) => (
        <Menu $size='small'>
          <Menu.Trigger>
            <KebabButton type='button' aria-label='Row actions'>
              <ThreeDotMenu />
            </KebabButton>
          </Menu.Trigger>
          <Menu.Content position='right'>
            <RemoveItem onClick={() => cellState(table).onRemove(original)}>
              <TrashIcon width={14} height={14} color={colors.red100} />
              {kind === 'payer' ? 'Remove facility from payer' : 'Remove payer from facility'}
            </RemoveItem>
          </Menu.Content>
        </Menu>
      ),
      size: 40,
      meta: { style: { ...ROW_PADDING, justifyContent: 'flex-end' } },
    }),
  ] as ColumnDef<GroupPayerNetworkStatus, any>[];
}

export default function NetworkStatusTable({
  kind,
  rows,
  loading,
  pageCount,
  pagination,
  sorting,
  selected,
  onSelectedChange,
  onRemove,
}: NetworkStatusTableProps) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.groupPayerNetworkStatusEdit);
  const queryClient = useQueryClient();
  // What each row's in-flight PATCH sent, by row id. It shows in place of the stale row until the
  // refetch lands, and a row takes no second edit meanwhile, so two PATCHes can't race.
  const [pending, setPending] = useState<Record<string, Partial<GroupPayerNetworkStatus>>>({});
  const { mutate } = useMutateWithToast({
    mutationFn: updateGroupPayerNetworkStatus,
    errorToast: (error) =>
      removedElsewhere(error)
        ? `This ${kind === 'payer' ? 'facility' : 'payer'} was removed by someone else. The list has been refreshed.`
        : validationErrorMessage(error),
    // Settled, not success: a row removed elsewhere 404s, and the refetch drops it from the page.
    onSettled: async (_data, _error, { id }) => {
      await queryClient.invalidateQueries({ queryKey: groupPayerNetworkStatusQueryKeys.base });
      setPending(({ [id]: _settled, ...rest }) => rest);
    },
  });

  const edit = (id: string, field: StatusField, value: NetworkStatus) => {
    if (pending[id]) return;
    const variables = { id, [field]: value };
    setPending((current) => ({ ...current, [id]: variables }));
    mutate(variables);
  };

  const allVisibleSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  // Union or subtract this page's rows, so selections on other pages, or hidden by a search, survive.
  const toggleAll = () => {
    const next = new Set(selected);
    rows.forEach((row) => (allVisibleSelected ? next.delete(row.id) : next.add(row.id)));
    onSelectedChange(next);
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange(next);
  };

  const columns = useMemo(() => columnsFor(kind, canEdit), [kind, canEdit]);

  const meta: CellState = {
    selected,
    allVisibleSelected,
    someVisibleSelected: rows.some((row) => selected.has(row.id)),
    pending,
    edit,
    toggle,
    toggleAll,
    onRemove,
  };

  return (
    <ControlledTable<GroupPayerNetworkStatus>
      data={rows}
      columns={columns}
      meta={meta}
      loading={loading}
      pageCount={pageCount}
      pagination={pagination}
      sorting={sorting}
      enableSortingRemoval={false}
    />
  );
}

type StatusToggleProps = {
  label: string;
  value: NetworkStatus;
  disabled?: boolean;
  onChange: (value: NetworkStatus) => void;
};

export function StatusToggle({ label, value, disabled, onChange }: StatusToggleProps) {
  return (
    <Toggle role='radiogroup' aria-label={label}>
      {STATUS_OPTIONS.map((option) => (
        <Segment
          key={option.label}
          type='button'
          role='radio'
          aria-checked={value === option.value}
          $status={value === option.value ? option.value : undefined}
          $width={SEGMENT_WIDTHS[option.label]}
          disabled={disabled}
          onClick={() => value !== option.value && onChange(option.value)}>
          {option.label}
        </Segment>
      ))}
    </Toggle>
  );
}

const Toggle = styled.div`
  display: inline-flex;
  box-sizing: border-box;
  height: 32px;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};
  overflow: hidden;
`;

// Figma's fixed segment widths, so the four columns' controls line up.
const SEGMENT_WIDTHS = { In: 32, Out: 41, 'Not Set': 56 } as Record<string, number>;

const SELECTED = {
  in: { background: colors.green25, color: colors.greenHover },
  out: { background: colors.red25, color: colors.redHover },
  notSet: { background: colors.black5, color: colors.black75 },
};

// `$status` is set only on the chosen segment.
const Segment = styled.button<{ $status?: NetworkStatus; $width: number }>`
  width: ${({ $width }) => $width}px;
  height: 100%;
  padding: 0;
  border: none;
  border-right: 1px solid ${colors.black10};
  font-family: inherit;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.15px;
  font-weight: ${({ $status }) => ($status === undefined ? 400 : 500)};
  color: ${({ $status }) => ($status === undefined ? colors.black100 : SELECTED[$status ?? 'notSet'].color)};
  background: ${({ $status }) => ($status === undefined ? colors.white : SELECTED[$status ?? 'notSet'].background)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  &:last-child {
    border-right: none;
  }
`;

const Name = styled(BodySmall)`
  font-weight: 500;
  color: ${colors.black100};
`;

// Checkbox draws the unchecked box in the inherited text color, which is black50 in an unsorted header.
const SelectCheckbox = styled(Checkbox)`
  color: ${colors.black100};
`;
